import { useSchoolYear } from '@pelckmans/business-components/context/SchoolYearContext';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Group } from '../../../../api/groups';
import ModalContext from '../../../../contexts/ModalContext';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import { getAssignmentsCountForGroup } from '../../api/MyGroupsApi';
import { SchoolYear } from '../../context/MyGroupsContext';
import useStudioGroup from '../../../../hooks/useStudioGroup';

type Props = {
  id: string;
  schoolYear: SchoolYear;
  group: Group;
  onConfirm: () => Promise<void>;
};

function useIsVOMultiYearActiveNextYear(group: Group) {
  const studioGroup = useStudioGroup();
  const { nextSchoolYear } = useSchoolYear();

  if (studioGroup !== 'VO' || !nextSchoolYear) return false;

  const [startYear, endYear] = group.schoolYear.split('-').map(Number);
  const isMultiYear = endYear - startYear > 1;

  return isMultiYear && endYear > nextSchoolYear.from.getFullYear();
}

function RemoveGroupConfirmationModal({ id, schoolYear, group, onConfirm }: Props) {
  const studioGroup = useStudioGroup();
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();
  const isCodeBasedGroup = studioGroup === 'VO' || studioGroup === 'BAO';
  const isMultiYearActiveNextYear = useIsVOMultiYearActiveNextYear(group);

  const { closeModal } = useContext(ModalContext);

  const [assignmentsCount, setAssignmentsCount] = useState<number>();

  const handleClose = () => {
    closeModal(id);
  };

  const handleDelete = async () => {
    await onConfirm();
  };

  useEffect(() => {
    if (isCodeBasedGroup) return;

    const getAssignmentsCount = async () => {
      const count = await getAssignmentsCountForGroup(schoolYear, group.id);

      setAssignmentsCount(count);
    };

    getAssignmentsCount().catch(handleError);
  }, [group, schoolYear, handleError, isCodeBasedGroup]);

  if (assignmentsCount === undefined && !isCodeBasedGroup) return null;

  const getCopy = () => {
    if (isMultiYearActiveNextYear) {
      return (
        <Trans
          i18nKey={'PAGES.MY_GROUPS.MODALS.REMOVE_GROUP_CONFIRMATION.BODY.MULTI_YEAR'}
          components={{
            a: <br />,
          }}
        />
      );
    }

    if (isCodeBasedGroup) {
      return t(`PAGES.MY_GROUPS.MODALS.REMOVE_GROUP_CONFIRMATION.BODY.${group.status!.toUpperCase()}`);
    }

    return assignmentsCount! > 0
      ? t('PAGES.MY_GROUPS.MODALS.REMOVE_GROUP_CONFIRMATION.BODY.TEXT_ASSIGNMENTS_IN_GROUP')
      : t('PAGES.MY_GROUPS.MODALS.REMOVE_GROUP_CONFIRMATION.BODY.TEXT');
  };

  return (
    <Dialog
      id={id}
      modal
      size="sm"
      onClose={handleClose}
      aria-label={t('PAGES.MY_GROUPS.MODALS.REMOVE_GROUP_CONFIRMATION.A11Y_LABEL')}
    >
      <Dialog.Header
        title={t(
          isMultiYearActiveNextYear
            ? 'PAGES.MY_GROUPS.MODALS.REMOVE_GROUP_CONFIRMATION.HEADER.TITLE_MULTI_YEAR'
            : 'PAGES.MY_GROUPS.MODALS.REMOVE_GROUP_CONFIRMATION.HEADER.TITLE',
        )}
        onClose={handleClose}
        aria-label={t('PAGES.MY_GROUPS.MODALS.REMOVE_GROUP_CONFIRMATION.HEADER.ACTIONS.CLOSE')}
        danger
      />
      <Dialog.Body>{getCopy()}</Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={{
          'aria-label': undefined,
          role: undefined,
          children: (
            <>
              <Button prominence="neutral" onClick={handleClose} key="cancel">
                {t('PAGES.MY_GROUPS.MODALS.REMOVE_GROUP_CONFIRMATION.FOOTER.ACTIONS.CANCEL')}
              </Button>
              <Button prominence="danger" onClick={handleDelete} key="confirm">
                {t('PAGES.MY_GROUPS.MODALS.REMOVE_GROUP_CONFIRMATION.FOOTER.ACTIONS.CONFIRM')}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}
export default RemoveGroupConfirmationModal;
